import React, { useEffect, useRef, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import * as currentUserSelectors from 'state/selectors/currentUserSelectors';
import * as currentUserActions from 'state/actions/currentUserActions';
import { I18N, I18NContext, GlobalContext } from 'commons';
import GoogleRecaptcha from 'commons/GoogleRecaptcha';
import { Ajax, orgId, memberApp, cookies } from 'utils';
import moment from 'moment';
import EBModal from 'commons/EBModal';
import { formatFormData } from 'utils/ebUtils';
import { parsePhoneNumber } from '@eb/libphonenumber'
import QuietTimeTag from 'components/forms/profile-form/QuietTimeTag';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExclamationTriangle, faCheckCircle } from '@fortawesome/free-solid-svg-icons'
import v from 'scss/v';
import { PREFERREDLANGUAGENOTPROVIDED } from 'constants/applicationConstant';

const MyProfileSection = ({
  getProfileInit,
  firstName,
  lastName,
  suffix,
  middleInitial,
  preferredLanguage,
  isPreferredLanguageExposed,
  externalId,
  timeZone,
  email,
  contactPaths,
  sendTestNotificationPermission: {
    recaptchaSiteKey,
    permission
  },
  recaptchaVerified,
  recaptchaVerifiedToken,
  hasSentTestNotification,
  sendTestNotification,
  isHome,
  sendErrorMsg,
  hasUniversalProfile
}) => {
  const alertRef = useRef();
  const {
    lang: currentLanguage,
    account: {
      id: contactId,
      name: username,
      isMigrated,
      registered,
    },
    organization: { userStatus, isUseDeviceTimeFrame, shouldShowPreferredLanguage },
    flags: {
          cme_mbr_delelteButton = false,
          PUBLIC_PORTAL_FORCE_LINKED_ACCOUNT_TO_UP
      },
    universalProfileUrl
  } = useContext(GlobalContext);
  const i18n = useContext(I18NContext);
  const { pathname } = window.location;
  let isShowTimezoneDiffWarning = false;
  const entryFromPublicPortal = cookies.get('entryFromPublicPortal', {
    path: `/${orgId}`
  });
  const [isDeleteAccountModalShow, setIsDeleteAccountModalShow] = useState(false);
  const formatNumber = (number, country) => {
    try {
      const parsedNumber = parsePhoneNumber(number, country.toUpperCase())
      return parsedNumber.format('INTERNATIONAL')
    } catch (e) {
      return number
    }
  };
  const processPathValue = (path) => {
    let pathValue = path.value;
    const numberTypeList = ['PHONE', 'SMS', 'FAX', 'TA', 'PAGER', 'TTYTDD'];
    if (path.type === 'RECIP') {
      return (pathValue === 'yes') ? i18n['ssp.main.login'] : i18n['ssp.myprofile.not.log'];
    }
    if (numberTypeList.indexOf(path.type) !== -1) {
      if (!path.noValidate) {
        pathValue = formatNumber(path.value, path.countryCode);
      }
    }
    return pathValue;
  };

  if (isUseDeviceTimeFrame) {
    // timezone is default to '', which makes contact offset 0
    // when first loading, set browser offset to 0, too
    const browserTimezoneOffset = timeZone ? moment.tz(moment.tz.guess()).format('Z') : '+00:00';
    const contactTimezoneOffset = moment.tz(timeZone).format('Z');
    isShowTimezoneDiffWarning = browserTimezoneOffset !== contactTimezoneOffset;
  }

  useEffect(() => {
    const loadProfile = async () => {
      try {
        await getProfileInit(contactId);
      } catch (error) {
        console.error('Error loading profile:', error);
      }
    };
    
    loadProfile();
  }, []);

  useEffect(() => {
    if (hasUniversalProfile && PUBLIC_PORTAL_FORCE_LINKED_ACCOUNT_TO_UP) {
      if (!entryFromPublicPortal) {
        const userConfirmed = window.confirm(i18n.get('profile.redirect.up.login'));
        if (userConfirmed) {
          window.location.href = universalProfileUrl;
        }
      }
    }
  }, [hasUniversalProfile]);

  const handleAccountDelete = () => {
    cookies.remove('orgId');
    cookies.remove('username');
    cookies.remove('password');
    cookies.remove('key');

    const url = `/deleteAccount/${orgId}`;
    const data = formatFormData({
      _method: 'DELETE'
    });
    Ajax.post(url, data).then(({ data: response }) => {
      if (response.status === 'yes') {
        if (entryFromPublicPortal) {
          window.location.href = `${universalProfileUrl}/home`;
        } else {
          window.location.reload();
        }
      } else {
        alert(i18n.get(response.errorCode));
        window.location.reload();
      }
    });
  };

  const onRecaptchaVerfied = value => {
    recaptchaVerified(value);
  };
  const onRecaptchaError = () => {
    console.log('Recaptch error!');
  };
  const onRecaptchaExpired = () => {
    console.log('Recaptch expired!');
  };

  const isPreferredLanguageVisible = shouldShowPreferredLanguage && isPreferredLanguageExposed;

  return (
    <div className="my-profile">
      <div className="profile-info">
        {
          registered && !isMigrated && !entryFromPublicPortal && !pathname.includes('portal-home') &&
          <div className="row">
            <span className="col-12 col-lg-5 text-lg-right label" id="username">
              <I18N>ssp.register.username</I18N>:
            </span>
            <span className="col-12 col-lg-7 notranslate" aria-labelledby="username" eb-test="ssp_username">{username}</span>
          </div>
        }
        {
          firstName &&
            <div className="row">
              <span className="col-12 col-lg-5 text-lg-right label" id="firstName">
                <I18N>ssp.register.firstname</I18N>:
              </span>
              <span className="col-12 col-lg-7 notranslate" aria-labelledby="firstName" eb-test="ssp_firstName">{firstName}</span>
            </div>
        }
        {
          lastName &&
            <div className="row">
              <span className="col-12 col-lg-5 text-lg-right label" id="lastName">
                <I18N>ssp.register.lastname</I18N>:
              </span>
              <span className="col-12 col-lg-7 notranslate" aria-labelledby="lastName" eb-test="ssp_lastName">{lastName}</span>
            </div>
        }
        {
          middleInitial &&
            <div className="row">
              <span className="col-12 col-lg-5 text-lg-right label" id="middleInitial">
                <I18N>ssp.profile.middle.initials</I18N>:
              </span>
              <span className="col-12 col-lg-7 notranslate" aria-labelledby="middleInitial" eb-test="ssp_middleInitial">{middleInitial}</span>
            </div>
        }
        {
          isPreferredLanguageVisible &&
            <div className="row">
              <span className="col-12 col-lg-5 text-lg-right label" id="lastName">
                <I18N>ssp.register.preferredLanguage</I18N>:
              </span>
              <span className="col-12 col-lg-7 notranslate" aria-labelledby="lastName" eb-test="ssp_lastName">{preferredLanguage || PREFERREDLANGUAGENOTPROVIDED}</span>
            </div>
        }
        {
          suffix &&
            <div className="row">
              <span className="col-12 col-lg-5 text-lg-right label" id="suffix">
                <I18N>ssp.profile.suffix</I18N>:
              </span>
              <span className="col-12 col-lg-7 notranslate" aria-labelledby="suffix" eb-test="ssp_suffix">{suffix}</span>
            </div>
        }
        {
          externalId &&
            <div className="row">
              <span className="col-12 col-lg-5 text-lg-right label" id="externalId">
                <I18N>ssp.profile.external.id</I18N>:
              </span>
              <span className="col-12 col-lg-7 notranslate" aria-labelledby="externalId">{externalId}</span>
            </div>
        }
        {
          email &&
            <div className="row">
              <span className="col-12 col-lg-5 text-lg-right label" id="registration-email">
                <I18N>ssp.register.registration.email</I18N>:
              </span>
              <span className="col-12 col-lg-7 notranslate" aria-labelledby="registration-email">{email}</span>
            </div>
        }
        {
          timeZone &&
            <div className="row">
              <span className="col-12 col-lg-5 text-lg-right label" id="timeZone">
                <I18N>contact.path.device.time.frame.quiet.time.timezone</I18N>
              </span>
              <span className="col-12 col-lg-7 notranslate" aria-labelledby="timeZone">{timeZone}</span>
            </div>
        }
        {
          isHome && isUseDeviceTimeFrame && isShowTimezoneDiffWarning &&
          <div className="col-12 alert alert-warning">
            <FontAwesomeIcon icon={faExclamationTriangle} color={v('$gold-8')} width="1em" height="1em" />
            <div className="ml-2">
              <b><I18N>home.alert.box.title.warning</I18N></b>
              <p className="mb-0"><I18N>home.profile.timezone.warning.text</I18N></p>
            </div>
          </div>
        }
      </div>
      {
        // eslint-disable-next-line camelcase
        isHome && userStatus === 'public' && (cme_mbr_delelteButton || !memberApp) &&
          <div className="delete-account">
            <button
              type="button"
              className="btn btn-secondary"
              eb-test="ssp_home_button_delete_account"
              onClick={() => { setIsDeleteAccountModalShow(true); }}
            >
              <I18N>ssp.delete.account</I18N>
            </button>
          </div>
      }
      <EBModal
        isOpen={isDeleteAccountModalShow}
        type="confirm"
        toggleModal={() => { setIsDeleteAccountModalShow(false); }}
        onConfirm={handleAccountDelete}
      >
        <div><I18N>ssp.delete.account.alert</I18N> </div>
      </EBModal>

      {
        contactPaths.length > 0 &&
          <div className="contact-paths">
            {contactPaths.map((path, index) => (processPathValue(path) ? (
              <div className="row" key={path.name}>
                <span className="col-12 col-lg-5 text-lg-right label">
                  {path.name}:
                </span>
                <div className="col-12 col-lg-7" aria-label={path.name}>
                  <div className="margin5-B">
                    {processPathValue(path)}
                  </div>
                  {
                    path.phoneExt &&
                    <div className="margin5-B">
                      <span className="sr-only">{path.name} - </span>
                      <I18N>
                        {path.ext ? 'template.myprofile.deliverypath.ext' : 'template.myprofile.deliverypath.pin'}
                      </I18N>: {path.phoneExt}
                    </div>
                  }
                  {
                    (path.pathType === 'PLAINTEXTEMAIL' || path.pathType === 'NUMERICPAGER') &&
                    path.systemRequirement != null &&
                    <div className="margin5-B">
                      <span className="sr-only">{path.name} - </span>
                      <I18N>template.myprofile.deliverypath.pagerservice</I18N>: {path.systemRequirement}
                    </div>
                  }
                  {
                    isHome && isUseDeviceTimeFrame &&
                  <>
                    <div className="margin5-B">
                      <span id={`status${index}`} className="label"><span className="sr-only">{path.name} - </span><I18N>ssp.myprofile.receive.notification</I18N>:</span>&nbsp;
                      <span aria-labelledby={`status${index}`}>{ path.pathEnable ? <I18N>global.on</I18N> : <I18N>global.off</I18N>}</span>
                    </div>
                    {
                      path.pathEnable && path.quietTimeFrames && path.quietTimeFrames.length > 0 &&
                        <div className="margin5-B">
                          <span id={`quietTime${index}`} className="label"><span className="sr-only">{path.name} - </span><I18N>contact.path.device.time.frame.quiet.time.dialog.title</I18N>:</span>
                          <div aria-labelledby={`quietTime${index}`} eb-test="quietTime">
                            {
                              path.quietTimeFrames.map((item, quietTimeIndex) => (
                                <QuietTimeTag
                                  pathId={path.pathId}
                                  id={quietTimeIndex}
                                  quietTime={item}
                                  timeZone={timeZone}
                                  key={`${path.pathId}-tag-${item.name}`}
                                  isHome
                                />
                              ))
                            }
                          </div>
                        </div>
                    }
                  </>
                  }
                </div>
              </div>
            ) : ''))}
          </div>
      }

      {
        permission && !memberApp &&
        <div className="send-test">
          {
            !hasSentTestNotification ?
              <>
                <div className="recaptcha-box text-center">
                  {
                    recaptchaSiteKey &&
                        <GoogleRecaptcha
                          sitekey={recaptchaSiteKey}
                          onChange={onRecaptchaVerfied}
                          onErrored={onRecaptchaError}
                          onExpired={onRecaptchaExpired}
                          hl={currentLanguage}
                        />
                  }
                </div>
                <div className="actions-box text-center">
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => {
                      sendTestNotification(contactId, currentLanguage, () => {
                        alertRef.current.focus();
                      });
                    }}
                    disabled={!recaptchaVerifiedToken}
                  >
                    <I18N>home.profile.button.sendTestNotification</I18N>
                  </button>
                </div>
              </>
              :
              <div role="alert" ref={(ref) => alertRef.current = ref}>
                {
                  sendErrorMsg ?
                    <div className="alert alert-warning text-left col-12" role="alert">
                      <FontAwesomeIcon icon={faExclamationTriangle} color={v('$gold-8')} width="1em" height="1em" />
                      <div className="align-top inline-block ml-2">
                        <b><I18N>home.alert.box.title.warning</I18N></b>
                        <p className="mb-0"><I18N>{sendErrorMsg}</I18N></p>
                      </div>
                    </div>
                    :
                    <div className="alert alert-success text-left col-12" role="alert">
                      <FontAwesomeIcon icon={faCheckCircle} color={v('$green-7')} width="1em" height="1em" />
                      <div className="align-top inline-block ml-2">
                        <b><I18N>home.alert.box.title.success</I18N></b>
                        <p className="mb-0"><I18N>ssp.register.review.hasSentTestNotification</I18N></p>
                      </div>
                    </div>
                }
              </div>
          }
        </div>
      }
    </div>
  );
};

MyProfileSection.propTypes = {
  getProfileInit: PropTypes.func.isRequired,
  recaptchaVerified: PropTypes.func.isRequired,
  sendTestNotification: PropTypes.func.isRequired,
  firstName: PropTypes.string.isRequired,
  lastName: PropTypes.string.isRequired,
  middleInitial: PropTypes.string.isRequired,
  shouldShowPreferredLanguage: PropTypes.bool,
  isPreferredLanguageExposed: PropTypes.bool.isRequired,
  preferredLanguage: PropTypes.string,
  suffix: PropTypes.string.isRequired,
  timeZone: PropTypes.string,
  email: PropTypes.string,
  externalId: PropTypes.string,
  contactPaths: PropTypes.arrayOf(PropTypes.object),
  sendTestNotificationPermission: PropTypes.shape({
    recaptchaSiteKey: PropTypes.string,
    permission: PropTypes.bool,
  }),
  recaptchaVerifiedToken: PropTypes.string,
  hasSentTestNotification: PropTypes.bool,
  isHome: PropTypes.bool,
  sendErrorMsg: PropTypes.string,
  hasUniversalProfile: PropTypes.bool
};

MyProfileSection.defaultProps = {
  timeZone: '',
  email: '',
  externalId: '',
  contactPaths: [],
  shouldShowPreferredLanguage: false,
  sendTestNotificationPermission: {},
  recaptchaVerifiedToken: null,
  hasSentTestNotification: false,
  isHome: false,
  sendErrorMsg: '',
  hasUniversalProfile: false
};

const mapStateToProps = state => ({
  firstName: currentUserSelectors.selectFirstName(state),
  lastName: currentUserSelectors.selectLastName(state),
  timeZone: currentUserSelectors.selectTimeZone(state),
  email: currentUserSelectors.selectEmail(state),
  externalId: currentUserSelectors.selectExternalId(state),
  middleInitial: currentUserSelectors.selectMiddleInitial(state),
  preferredLanguage: currentUserSelectors.selectPreferredLanguage(state),
  isPreferredLanguageExposed: currentUserSelectors.selectIsPreferredLanguageExposed(state),
  suffix: currentUserSelectors.selectSuffix(state),
  contactPaths: currentUserSelectors.selectContactPaths(state),
  sendTestNotificationPermission: currentUserSelectors.selectSendNotificationPermission(state),
  recaptchaVerifiedToken: currentUserSelectors.selectRecaptchaVerifedToken(state),
  hasSentTestNotification: currentUserSelectors.selectHasSentTestNotification(state),
  sendErrorMsg: currentUserSelectors.selectSendErrorMsg(state),
  hasUniversalProfile: currentUserSelectors.selectHasUniversalProfile(state)
});

const mapDispatchToProps = dispatch => ({
  getProfileInit: (contactId) => dispatch(currentUserActions.getProfileInit.action({ contactId })),
  recaptchaVerified: (value) => dispatch(currentUserActions.recaptchaVerified.action(value)),
  sendTestNotification: (contactId, lang, callback) =>
    dispatch(currentUserActions.sendTestNotification.action({ contactId, lang, callback })),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyProfileSection);
